import './SessionHistoryPanel.scss';
import cn from 'classnames';
import { ReactNode, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { FaCheck } from 'react-icons/fa';
import {
  MdArrowBack,
  MdClose,
  MdEdit,
  MdInventory,
  MdOutlineAddComment,
  MdOutlineHistory,
  MdOutlineInventory2,
  MdOutlinePushPin
} from 'react-icons/md';
import { LLMSession, SessionId, UpdateSessionArgs } from '@blisspointmedia/bpm-types/dist/LLM';
import { Appearance, Design, Placement, Size } from '@Components/designConstants';
import { Dropdown, DropdownToggleType, MenuItem } from '@Components/Dropdown';
import { IconButton } from '@Components/IconButton';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { SearchField } from '@Components/SearchField/SearchField';
import { Spinner } from '@Components/Spinner';
import { useFilteredList } from '@src/utils/search';

const SessionAction = {
  PIN: 'pin',
  UNPIN: 'unpin',
  RENAME: 'rename',
  ARCHIVE: 'archive',
  UNARCHIVE: 'unarchive'
} as const;
type SessionActionType = (typeof SessionAction)[keyof typeof SessionAction];

// Local mirror of bpm-types' LLMSessionState ('PINNED' | 'ARCHIVED' | null) — kept here rather
// than in bpm-types since only this file compares against the non-null members.
const SessionState = {
  PINNED: 'PINNED',
  ARCHIVED: 'ARCHIVED'
} as const;

const SESSION_MENU_ITEMS: Record<SessionActionType, MenuItem<SessionActionType>> = {
  [SessionAction.PIN]: {
    value: SessionAction.PIN,
    label: 'Pin',
    leadingContent: <MdOutlinePushPin />
  },
  [SessionAction.UNPIN]: {
    value: SessionAction.UNPIN,
    label: 'Unpin',
    leadingContent: <MdOutlinePushPin />
  },
  [SessionAction.RENAME]: {
    value: SessionAction.RENAME,
    label: 'Rename',
    leadingContent: <MdEdit />
  },
  [SessionAction.ARCHIVE]: {
    value: SessionAction.ARCHIVE,
    label: 'Archive',
    leadingContent: <MdInventory />
  },
  [SessionAction.UNARCHIVE]: {
    value: SessionAction.UNARCHIVE,
    label: 'Unarchive',
    leadingContent: <MdInventory />
  }
};

const actionsForSession = (session: LLMSession): MenuItem<SessionActionType>[] => [
  session.state === SessionState.PINNED
    ? SESSION_MENU_ITEMS[SessionAction.UNPIN]
    : SESSION_MENU_ITEMS[SessionAction.PIN],
  SESSION_MENU_ITEMS[SessionAction.RENAME],
  session.state === SessionState.ARCHIVED
    ? SESSION_MENU_ITEMS[SessionAction.UNARCHIVE]
    : SESSION_MENU_ITEMS[SessionAction.ARCHIVE]
];

interface SessionHistoryPanelProps {
  sessions: LLMSession[] | undefined;
  currentSessionId: string;
  onSelectSession: (sessionId: SessionId) => void;
  onStartNewSession: () => void;
  onUpdateSession: (args: UpdateSessionArgs) => Promise<void>;
  onClose: () => void;
}

export const SessionHistoryPanel = ({
  sessions,
  currentSessionId,
  onSelectSession,
  onStartNewSession,
  onUpdateSession,
  onClose
}: SessionHistoryPanelProps) => {
  const [searchInput, setSearchInput] = useState('');
  const [searchArchiveInput, setSearchArchiveInput] = useState('');
  const [showArchive, setShowArchive] = useState(false);
  const [renamingSessionId, setRenamingSessionId] = useState<SessionId | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [submittingSessionIds, setSubmittingSessionIds] = useState<Set<SessionId>>(new Set());

  const runUpdate = async (args: UpdateSessionArgs) => {
    setSubmittingSessionIds(prev => new Set(prev).add(args.sessionId));
    try {
      await onUpdateSession(args);
    } catch (e) {
      console.error('Failed to update session:', e);
    } finally {
      setSubmittingSessionIds(prev => {
        const next = new Set(prev);
        next.delete(args.sessionId);
        return next;
      });
    }
  };

  const startRename = (session: LLMSession) => {
    setRenamingSessionId(session.sessionId);
    setRenameDraft(session.sessionName);
  };
  const cancelRename = () => setRenamingSessionId(null);
  const confirmRename = (sessionId: SessionId) => {
    const newName = renameDraft.trim();
    if (!newName) return;
    setRenamingSessionId(null);
    runUpdate({ sessionId, newName });
  };

  const runAction = (action: SessionActionType, session: LLMSession) => {
    if (action === SessionAction.RENAME) {
      startRename(session);
      return;
    }
    if (action === SessionAction.PIN) {
      runUpdate({ sessionId: session.sessionId, newState: SessionState.PINNED });
      return;
    }
    if (action === SessionAction.ARCHIVE) {
      runUpdate({ sessionId: session.sessionId, newState: SessionState.ARCHIVED });
      return;
    }
    // UNPIN and UNARCHIVE both just clear the state back to plain history.
    runUpdate({ sessionId: session.sessionId, newState: null });
  };

  // Most-recent-first — the backend doesn't guarantee an order.
  const sessionsArr = useMemo(
    () => [...(sessions ?? [])].sort((a, b) => b.timestamp - a.timestamp),
    [sessions]
  );

  const pinnedSessionsAll = useMemo(
    () => sessionsArr.filter(s => s.state === SessionState.PINNED),
    [sessionsArr]
  );
  const pinnedSessions = useFilteredList(pinnedSessionsAll, searchInput, {
    keys: ['sessionName']
  });
  const pinnedEmptyMessage =
    pinnedSessionsAll.length === 0 ? 'No pinned chats yet' : 'No matching results';

  const historicalSessionsAll = useMemo(
    () => sessionsArr.filter(s => s.state === null),
    [sessionsArr]
  );
  const historicalSessions = useFilteredList(historicalSessionsAll, searchInput, {
    keys: ['sessionName']
  });
  let historicalEmptyMessage = 'No matching results';
  if (historicalSessionsAll.length === 0) {
    historicalEmptyMessage =
      sessionsArr.length === 0 ? 'No chat history yet' : 'Nothing to show here';
  }

  const archivedSessionsAll = useMemo(
    () => sessionsArr.filter(s => s.state === SessionState.ARCHIVED),
    [sessionsArr]
  );
  const archivedSessions = useFilteredList(archivedSessionsAll, searchArchiveInput, {
    keys: ['sessionName']
  });
  const archivedEmptyMessage =
    archivedSessionsAll.length === 0 ? 'No archived chats yet' : 'No matching results';

  const sharedRowProps = {
    currentSessionId,
    onSelectSession,
    renamingSessionId,
    renameDraft,
    setRenameDraft,
    confirmRename,
    cancelRename,
    submittingSessionIds,
    runAction
  };

  if (sessions === undefined) {
    return (
      <Panel onClose={onClose}>
        <div className='panel-loading'>
          <Spinner />
        </div>
      </Panel>
    );
  }

  if (showArchive) {
    return (
      <Panel onClose={onClose}>
        <InteractiveDiv className='archive-back' onClick={() => setShowArchive(false)}>
          <MdArrowBack />
          Archived
        </InteractiveDiv>
        <SearchInput
          placeholder='Search Archive'
          value={searchArchiveInput}
          onChange={setSearchArchiveInput}
        />
        <SessionSection
          sessions={archivedSessions}
          emptyMessage={archivedEmptyMessage}
          {...sharedRowProps}
        />
      </Panel>
    );
  }

  return (
    <Panel onClose={onClose}>
      <SearchInput placeholder='Search Chats' value={searchInput} onChange={setSearchInput} />
      <InteractiveDiv className='new-chat-row' onClick={onStartNewSession}>
        <MdOutlineAddComment />
        New Chat
      </InteractiveDiv>
      <div className='panel-body'>
        <SectionHeader text='Pinned' />
        <SessionSection
          sessions={pinnedSessions}
          emptyMessage={pinnedEmptyMessage}
          {...sharedRowProps}
        />
        <SectionHeader text='History' />
        <SessionSection
          sessions={historicalSessions}
          emptyMessage={historicalEmptyMessage}
          {...sharedRowProps}
        />
        <InteractiveDiv className='archived-link' onClick={() => setShowArchive(true)}>
          <MdOutlineInventory2 />
          Archived
        </InteractiveDiv>
      </div>
    </Panel>
  );
};

interface PanelProps {
  onClose: () => void;
  children: ReactNode;
}

// Shared by all three panel states (loading / archive view / main view) so the
// header + container markup lives in one place.
const Panel = ({ onClose, children }: PanelProps) => (
  <div className='session-history-panel'>
    <div className='panel-header'>
      <MdOutlineHistory />
      <span>Chat History</span>
      <IconButton
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        size={Size.SM}
        onClick={onClose}
        aria-label='Close chat history'
      >
        <MdClose />
      </IconButton>
    </div>
    {children}
  </div>
);

interface SearchInputProps {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}

const SearchInput = ({ placeholder, value, onChange }: SearchInputProps) => (
  <SearchField
    className='panel-search'
    design={Design.NEUTRAL}
    size={Size.SM}
    placeholder={placeholder}
    value={value}
    onChange={e => onChange(e.target.value)}
  />
);

interface SectionHeaderProps {
  text: string;
}

const SectionHeader = ({ text }: SectionHeaderProps) => (
  <div className='section-header'>{text}</div>
);

interface SessionSectionProps {
  sessions: readonly LLMSession[];
  emptyMessage: string;
  currentSessionId: string;
  onSelectSession: (sessionId: SessionId) => void;
  renamingSessionId: SessionId | null;
  renameDraft: string;
  setRenameDraft: (value: string) => void;
  confirmRename: (sessionId: SessionId) => void;
  cancelRename: () => void;
  submittingSessionIds: Set<SessionId>;
  runAction: (action: SessionActionType, session: LLMSession) => void;
}

const SessionSection = ({ sessions, emptyMessage, ...rowProps }: SessionSectionProps) => {
  if (sessions.length === 0) {
    return <div className='section-empty'>{emptyMessage}</div>;
  }

  return (
    <div className='session-list'>
      {sessions.map(session => (
        <SessionRow key={session.sessionId} session={session} {...rowProps} />
      ))}
    </div>
  );
};

interface SessionRowProps {
  session: LLMSession;
  currentSessionId: string;
  onSelectSession: (sessionId: SessionId) => void;
  renamingSessionId: SessionId | null;
  renameDraft: string;
  setRenameDraft: (value: string) => void;
  confirmRename: (sessionId: SessionId) => void;
  cancelRename: () => void;
  submittingSessionIds: Set<SessionId>;
  runAction: (action: SessionActionType, session: LLMSession) => void;
}

const SessionRow = ({
  session,
  currentSessionId,
  onSelectSession,
  renamingSessionId,
  renameDraft,
  setRenameDraft,
  confirmRename,
  cancelRename,
  submittingSessionIds,
  runAction
}: SessionRowProps) => {
  const { sessionId, sessionName } = session;
  const isActive = sessionId === currentSessionId;
  const isRenaming = renamingSessionId === sessionId;
  const isSubmitting = submittingSessionIds.has(sessionId);

  return (
    <InteractiveDiv
      className={cn('session-row', { active: isActive, renaming: isRenaming })}
      disabled={isActive || isRenaming || isSubmitting}
      onClick={() => onSelectSession(sessionId)}
    >
      {isRenaming ? (
        <RenameControls
          sessionId={sessionId}
          renameDraft={renameDraft}
          setRenameDraft={setRenameDraft}
          confirmRename={confirmRename}
          cancelRename={cancelRename}
        />
      ) : (
        <>
          <span className='session-name'>{sessionName}</span>
          {/* onClickCapture stops the click before it bubbles to the row's onClick (select
              session) — the row wraps these actions so they need to sit outside its normal
              click target. */}
          <div className='session-row-actions' onClickCapture={e => e.stopPropagation()}>
            <SessionRowActions
              session={session}
              isSubmitting={isSubmitting}
              runAction={runAction}
            />
          </div>
        </>
      )}
    </InteractiveDiv>
  );
};

interface RenameControlsProps {
  sessionId: SessionId;
  renameDraft: string;
  setRenameDraft: (value: string) => void;
  confirmRename: (sessionId: SessionId) => void;
  cancelRename: () => void;
}

// Owns both the rename input and its confirm/cancel actions together, rather than splitting
// them across SessionRow and SessionRowActions — the two only ever appear as a pair.
const RenameControls = ({
  sessionId,
  renameDraft,
  setRenameDraft,
  confirmRename,
  cancelRename
}: RenameControlsProps) => (
  <>
    <Form.Control
      className='rename-input'
      value={renameDraft}
      maxLength={256}
      autoFocus
      onClick={e => e.stopPropagation()}
      onChange={e => setRenameDraft(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          confirmRename(sessionId);
        } else if (e.key === 'Escape') {
          cancelRename();
        }
      }}
    />
    <div className='session-row-actions' onClickCapture={e => e.stopPropagation()}>
      <IconButton
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        size={Size.SM}
        disabled={!renameDraft.trim()}
        onClick={() => confirmRename(sessionId)}
        aria-label='Confirm rename'
      >
        <FaCheck size={12} />
      </IconButton>
      <IconButton
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        size={Size.SM}
        onClick={cancelRename}
        aria-label='Cancel rename'
      >
        <MdClose />
      </IconButton>
    </div>
  </>
);

interface SessionRowActionsProps {
  session: LLMSession;
  isSubmitting: boolean;
  runAction: (action: SessionActionType, session: LLMSession) => void;
}

const SessionRowActions = ({ session, isSubmitting, runAction }: SessionRowActionsProps) => {
  if (isSubmitting) return <Spinner size={18} />;

  return (
    <Dropdown
      type={DropdownToggleType.ICON_ONLY}
      overlayPlacement={Placement.RIGHT}
      size={Size.LG}
      className='session-actions-dropdown'
      options={actionsForSession(session)}
      onChange={action => runAction(action, session)}
      leadingIcon={<BsThreeDotsVertical />}
      placeholder=''
    />
  );
};
